import "../styles/app.css";
import { api, ApiError } from "./api";
import { iniciarRouter, navegar, type Ruta } from "./router";
import type { Sesion } from "../lib/tipos";
import { renderLogin } from "./pages/login";
import { renderAgenda } from "./pages/agenda";
import { renderProyectos } from "./pages/proyectos";
import { renderProyecto } from "./pages/proyecto";
import { renderRecordatorios } from "./pages/recordatorios";
import { renderPresupuestos } from "./pages/presupuestos";
import { renderPresupuesto } from "./pages/presupuesto";
import { renderContratos } from "./pages/contratos";
import { renderContrato } from "./pages/contrato";
import { renderClientes } from "./pages/clientes";
import { renderCondiciones } from "./pages/condiciones";
import { renderGrafo } from "./pages/grafo";
import { renderAyuda } from "./pages/ayuda";
import { renderAuditoria } from "./pages/auditoria";
import { renderCuenta } from "./pages/cuenta";
import { renderCapturar } from "./pages/capturar";
import { renderBandeja } from "./pages/bandeja";
import {
  logoApp,
  iconoSalir,
  iconoMenu,
  iconoCerrar,
  iconoFlechaDer,
  iconoAyuda,
  iconoMicrofono,
} from "./iconos";

const nav = document.getElementById("nav") as HTMLElement;
const app = document.getElementById("app") as HTMLElement;

// La primera es la pantalla de entrada.
const RUTAS: Ruta[] = [
  { patron: "#/agenda", titulo: "Hoy", render: renderAgenda },
  { patron: "#/proyectos", titulo: "Proyectos", render: renderProyectos },
  { patron: "#/presupuestos", titulo: "Precios", render: renderPresupuestos },
  { patron: "#/clientes", titulo: "Clientes", render: renderClientes },
  { patron: "#/condiciones", titulo: "Cláusulas", render: renderCondiciones },
  { patron: "#/recordatorios", titulo: "Recordar", render: renderRecordatorios },
  { patron: "#/grafo", titulo: "Grafo", render: renderGrafo },
  { patron: "#/grafo/:id", titulo: "Grafo", render: renderGrafo, oculta: true },
  { patron: "#/capturar", titulo: "Captura rápida", render: renderCapturar, oculta: true },
  { patron: "#/bandeja", titulo: "Bandeja de entrada", render: renderBandeja, oculta: true },
  { patron: "#/cuenta", titulo: "Mi clave y usuarios", render: renderCuenta, oculta: true },
  { patron: "#/ayuda", titulo: "Cómo se usa", render: renderAyuda, oculta: true },
  { patron: "#/auditoria", titulo: "Registro", render: renderAuditoria, oculta: true },
  { patron: "#/proyecto/:id", titulo: "Proyecto", render: renderProyecto, oculta: true },
  { patron: "#/presupuesto/:id", titulo: "Presupuesto", render: renderPresupuesto, oculta: true },
  // Sub-pantallas de Precios: se llega por el conmutador, no por la barra.
  { patron: "#/contratos", titulo: "Contratos", render: renderContratos, oculta: true },
  { patron: "#/contrato/:id", titulo: "Contrato", render: renderContrato, oculta: true },
];

// El menu movil vive en el body, no adentro del header: el header tiene
// backdrop-blur, y eso crea un bloque contenedor que se come el position:fixed
// del overlay (AGENTS.md seccion 8).
function marcarActivo(): void {
  const actual = location.hash || RUTAS[0].patron;
  document.querySelectorAll<HTMLAnchorElement>("a[data-hash]").forEach((a) => {
    a.classList.toggle("activo", a.dataset.hash === actual);
  });
}

function renderNav(usuario: string): void {
  const visibles = RUTAS.filter((r) => !r.oculta);

  const linksEscritorio = visibles
    .map(
      (r) =>
        `<a href="${r.patron}" data-hash="${r.patron}"
            class="nav-link rounded-lg px-2.5 py-1.5 text-apagado transition hover:bg-realce hover:text-texto">${r.titulo}</a>`,
    )
    .join("");

  // En el celular los renglones son grandes: se toca con el pulgar, parado.
  const linksMovil = visibles
    .map(
      (r) =>
        `<a href="${r.patron}" data-hash="${r.patron}"
            class="nav-movil flex items-center justify-between rounded-xl px-4 py-4 text-lg font-medium text-texto transition hover:bg-realce active:bg-borde">
           <span>${r.titulo}</span>
           <span class="text-apagado">${iconoFlechaDer}</span>
         </a>`,
    )
    .join("");

  nav.innerHTML = `
    <div class="mx-auto flex max-w-3xl items-center gap-1 px-3 py-2">
      <a href="#/agenda" class="flex flex-1 shrink-0 items-center gap-2 font-bold sm:flex-none" title="Míster Memoria">
        ${logoApp()}<span>Míster Memoria</span>
      </a>
      <nav class="hidden flex-1 items-center gap-0.5 pl-2 text-sm sm:flex">${linksEscritorio}</nav>
      <a href="#/ayuda" class="hidden shrink-0 rounded-lg p-2 text-apagado transition hover:bg-realce hover:text-texto sm:inline-flex"
         title="Cómo se usa" aria-label="Cómo se usa">${iconoAyuda}</a>
      <a href="#/cuenta" class="hidden text-sm text-apagado transition hover:text-texto sm:inline"
         title="Mi clave y usuarios">${usuario}</a>
      <button id="salir" class="hidden shrink-0 rounded-lg p-2 text-apagado transition hover:bg-realce hover:text-texto sm:inline-flex"
              aria-label="Salir" title="Salir">${iconoSalir}</button>
      <button id="hamb" class="shrink-0 rounded-lg p-2 text-apagado transition hover:bg-realce hover:text-texto sm:hidden"
              aria-label="Abrir menú">${iconoMenu}</button>
    </div>`;

  document.getElementById("menuMovil")?.remove();
  const menu = document.createElement("div");
  menu.id = "menuMovil";
  menu.className = "fixed inset-0 z-50 hidden sm:hidden";
  menu.innerHTML = `
    <div id="fondoMenu" class="absolute inset-0 bg-black/60"></div>
    <div class="absolute inset-x-0 top-0 max-h-full overflow-y-auto rounded-b-2xl border-b border-borde bg-superficie shadow-2xl">
      <div class="flex items-center justify-between border-b border-borde px-4 py-3">
        <span class="flex items-center gap-2 font-bold">${logoApp()} Míster Memoria</span>
        <button id="cerrarMenu" class="rounded-lg p-2 text-apagado transition hover:bg-realce hover:text-texto"
                aria-label="Cerrar menú">${iconoCerrar}</button>
      </div>
      <nav class="flex flex-col gap-1 p-2">${linksMovil}</nav>
      <nav class="flex flex-col gap-1 border-t border-borde p-2">
        ${["#/cuenta", "#/ayuda", "#/auditoria"]
          .map((h) => {
            const r = RUTAS.find((x) => x.patron === h) as Ruta;
            return `<a href="${h}" data-hash="${h}"
                       class="nav-movil flex items-center justify-between rounded-xl px-4 py-3 text-apagado transition hover:bg-realce">
                      <span>${r.titulo}</span>
                      <span>${iconoFlechaDer}</span>
                    </a>`;
          })
          .join("")}
      </nav>
      <div class="flex items-center justify-between border-t border-borde px-4 py-3">
        <span class="text-sm text-apagado">Sesión: <b class="text-texto">${usuario}</b></span>
        <button id="salirMovil" class="btn-sec">Salir</button>
      </div>
    </div>`;
  document.body.appendChild(menu);

  const abrir = () => menu.classList.remove("hidden");
  const cerrar = () => menu.classList.add("hidden");

  nav.querySelector("#hamb")?.addEventListener("click", abrir);
  menu.querySelector("#cerrarMenu")?.addEventListener("click", cerrar);
  menu.querySelector("#fondoMenu")?.addEventListener("click", cerrar);
  // Elegir una pantalla cierra el menu, aunque sea la que ya estaba abierta.
  menu.querySelectorAll("a[data-hash]").forEach((a) => a.addEventListener("click", cerrar));
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") cerrar();
  });

  const salir = async () => {
    await api.post("/api/logout");
    location.reload();
  };
  nav.querySelector("#salir")?.addEventListener("click", salir);
  menu.querySelector("#salirMovil")?.addEventListener("click", salir);

  marcarActivo();
  window.addEventListener("hashchange", marcarActivo);
}

async function iniciar(): Promise<void> {
  try {
    const s = await api.get<Sesion>("/api/sesion");
    renderNav(s.usuario);
    montarBotonCaptura();
    iniciarRouter(RUTAS, app);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      nav.innerHTML = "";
      renderLogin(app, () => {
        void iniciar();
      });
    } else {
      app.innerHTML = `<div class="tarjeta text-alerta">Error al iniciar: ${
        e instanceof Error ? e.message : "desconocido"
      }</div>`;
    }
  }
}

/**
 * Botón flotante de captura, visible en todas las pantallas.
 *
 * Se monta en el body y no dentro del header: el header tiene backdrop-blur,
 * y eso crea un bloque contenedor que se come el position:fixed
 * (AGENTS.md sección 8).
 */
function montarBotonCaptura(): void {
  document.getElementById("fabCaptura")?.remove();
  const a = document.createElement("a");
  a.id = "fabCaptura";
  a.href = "#/capturar";
  a.title = "Captura rápida";
  a.setAttribute("aria-label", "Captura rápida");
  a.className =
    "fixed bottom-5 right-5 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full " +
    "bg-acento-700 text-white shadow-lg transition hover:bg-acento-600 active:scale-95";
  a.innerHTML = iconoMicrofono;
  document.body.appendChild(a);

  // En la propia pantalla de captura estorba.
  const actualizar = () => a.classList.toggle("hidden", location.hash === "#/capturar");
  window.addEventListener("hashchange", actualizar);
  actualizar();
}

// Aviso de sin señal. No bloquea nada: la app sigue mostrando lo cacheado.
function vigilarConexion(): void {
  const aviso = document.getElementById("sinConexion") as HTMLElement;
  const actualizar = () => aviso.classList.toggle("hidden", navigator.onLine);
  window.addEventListener("online", actualizar);
  window.addEventListener("offline", actualizar);
  actualizar();
}

// El service worker se registra despues de cargar la pagina: no tiene que
// competir por ancho de banda con lo que se esta mostrando.
function registrarWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}

// Expuesto por si alguna pantalla necesita navegar desde un handler suelto.
(window as unknown as { navegar: typeof navegar }).navegar = navegar;

vigilarConexion();
registrarWorker();
void iniciar();
